import type { AdminGenerationDetail, AdminGenerationRow, AdminGenStatus, AdminTask } from '@trendator/shared';
import { useEffect, useState } from 'react';
import { Link } from '../components/Layout';
import { Button, Card, Empty, ErrorBox, FilterButton, Input, Label, Loading, Modal, Select, Status, Table, useToast, type Tone } from '../components/ui';
import { cn } from '../lib/cn';
import { api, errorText } from '../lib/api';
import { dateTime, num, rub, seconds, shortId, time, usd, userName, when } from '../lib/format';
import { useLoad, usePoll } from '../lib/hooks';
import { setQuery } from '../lib/router';
import { ResultMedia } from './Trends';

export const GEN_STATUS: Record<AdminGenStatus, { label: string; tone: Tone }> = {
  succeeded: { label: 'Готово', tone: 'ok' },
  running: { label: 'Рисуем', tone: 'run' },
  queued: { label: 'В очереди', tone: 'idle' },
  stuck: { label: 'Зависла', tone: 'warn' },
  failed: { label: 'Возврат', tone: 'bad' },
};
const PROVIDER: Record<string, string> = { kie: 'kie.ai', fal: 'fal.ai' };
const FILTERS = [
  ['all', 'Все'],
  ['run', 'В работе'],
  ['stuck', 'Зависли'],
  ['fail', 'Возвраты'],
  ['done', 'Готово'],
] as const;

type Counts = Record<(typeof FILTERS)[number][0], number>;

export function genSum(g: Pick<AdminGenerationRow, 'free' | 'test' | 'price' | 'refunded'>): string {
  if (g.test) return 'тест';
  if (g.free) return 'бесплатно';
  return rub(g.price);
}

export function Generations({ query }: { query: URLSearchParams }) {
  const filter = query.get('filter') ?? 'all';
  const source = query.get('source') ?? '';
  const provider = query.get('provider') ?? '';
  const period = query.get('period') ?? '';
  const q = query.get('q') ?? '';
  const sel = query.get('id');
  const [search, setSearch] = useState(q);

  const qs = new URLSearchParams({ filter, ...(source && { source }), ...(provider && { provider }), ...(period && { period }), ...(q && { q }) }).toString();
  const list = useLoad(() => api.get<{ items: AdminGenerationRow[]; counts: Counts }>(`/generations?${qs}`), [qs]);
  usePoll(() => void list.reload(), 15_000, true);

  useEffect(() => {
    const t = setTimeout(() => search !== q && setQuery({ q: search.trim() || null }), 400);
    return () => clearTimeout(t);
  }, [search, q]);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3.5">
        <h1 className="m-0 text-2xl font-extrabold tracking-tight lg:text-[30px]">Генерации</h1>
        <label className="min-w-[220px] flex-[0_1_360px]">
          <span className="sr-only">Поиск</span>
          <Input placeholder="ID, @юзер или task id провайдера" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-card text-sm" />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([id, label]) => (
          <FilterButton key={id} on={filter === id} onClick={() => setQuery({ filter: id === 'all' ? null : id })}>
            {label} <span className="opacity-70">{list.data ? num(list.data.counts[id]) : ''}</span>
          </FilterButton>
        ))}
        <span className="mx-1 hidden w-px bg-white/8 sm:block" />
        <Select
          value={source}
          onChange={(v) => setQuery({ source: v || null })}
          options={[
            { value: '', label: 'Тип: все' },
            { value: 'trend', label: 'Тренды' },
            { value: 'manual', label: 'Ручной режим' },
          ]}
          className="h-9 w-auto bg-card text-sm font-semibold"
        />
        <Select
          value={provider}
          onChange={(v) => setQuery({ provider: v || null })}
          options={[
            { value: '', label: 'Провайдер: все' },
            { value: 'kie', label: 'kie.ai' },
            { value: 'fal', label: 'fal.ai' },
          ]}
          className="h-9 w-auto bg-card text-sm font-semibold"
        />
        <Select
          value={period}
          onChange={(v) => setQuery({ period: v || null })}
          options={[
            { value: '', label: 'За всё время' },
            { value: 'today', label: 'Сегодня' },
            { value: '7d', label: '7 дней' },
            { value: '30d', label: '30 дней' },
          ]}
          className="h-9 w-auto bg-card text-sm font-semibold"
        />
      </div>

      <div className="flex flex-wrap items-start gap-[18px]">
        <Card className={cn('min-w-0 flex-[3_1_560px] px-2 pt-2 pb-1', sel && 'hidden lg:block')}>
          {list.error ? <ErrorBox text={list.error} onRetry={() => void list.reload()} /> : null}
          {!list.data && list.loading ? <Loading /> : null}
          {list.data && list.data.items.length === 0 ? <Empty>Ничего не нашли</Empty> : null}
          {list.data && list.data.items.length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Юзер</th>
                  <th>Что</th>
                  <th>Провайдер</th>
                  <th>Статус</th>
                  <th className="text-right!">Сумма</th>
                  <th className="text-right!">Время</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((r) => (
                  <tr key={r.id} onClick={() => setQuery({ id: r.id })} className={cn('cursor-pointer hover:[&_td]:bg-white/3', sel === r.id && '[&_td]:bg-accent/8')}>
                    <td className="font-mono text-xs text-muted">#{shortId(r.id)}</td>
                    <td className="font-semibold">{userName(r.user)}</td>
                    <td>
                      <span className="flex flex-col">
                        <span>
                          {r.title}
                          {r.test ? <span className="ml-1.5 rounded bg-warn/14 px-1.5 text-[11px] font-bold text-warn">ТЕСТ</span> : null}
                        </span>
                        <span className="text-xs text-muted">{r.kindLabel}</span>
                      </span>
                    </td>
                    <td className="text-soft">{r.providers.map((p) => PROVIDER[p] ?? p).join(' → ') || '—'}</td>
                    <td>
                      <Status tone={GEN_STATUS[r.status].tone}>{GEN_STATUS[r.status].label}</Status>
                    </td>
                    <td className="text-right font-semibold">{genSum(r)}</td>
                    <td className="text-right text-muted">{when(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </Card>

        {sel ? <GenerationPanel key={sel} id={sel} onChanged={() => void list.reload()} /> : null}
      </div>
    </>
  );
}

function taskTone(t: AdminTask): Tone {
  if (t.status === 'succeeded') return 'ok';
  if (t.status === 'failed') return 'bad';
  if (t.status === 'submitted') return 'run';
  return 'idle';
}
const TONE_BG: Record<Tone, string> = { ok: 'bg-ok', bad: 'bg-bad', run: 'bg-accent', idle: 'bg-text2/50', warn: 'bg-warn' };
const ERR: Record<string, string> = {
  provider_failed: 'Модель не ответила',
  admin_cancel: 'Остановлено из админки',
  admin_restart: 'Перезапущено из админки',
};

function GenerationPanel({ id, onChanged }: { id: string; onChanged: () => void }) {
  const toast = useToast();
  const d = useLoad(() => api.get<AdminGenerationDetail>(`/generations/${id}`), [id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'refund' | null>(null);
  const g = d.data;
  usePoll(() => void d.reload(), 5000, !!g && (g.status === 'queued' || g.status === 'running'));

  const act = async (name: string, fn: () => Promise<AdminGenerationDetail>, ok: string) => {
    setBusy(name);
    try {
      d.setData(await fn());
      onChanged();
      toast(ok);
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  if (d.error && !g) return <ErrorBox text={d.error} onRetry={() => void d.reload()} />;
  if (!g) {
    return (
      <Card className="flex-[1_1_320px]">
        <Loading />
      </Card>
    );
  }
  const st = GEN_STATUS[g.status];
  const open = g.status === 'queued' || g.status === 'running' || g.status === 'stuck';
  const firstErr = g.tasks.find((t) => t.error && t.error !== 'admin_restart')?.error ?? g.error;
  const steps = [...new Set(g.tasks.map((t) => t.step))];

  return (
    <Card className="flex min-w-0 flex-[1_1_320px] flex-col gap-4">
      <button type="button" onClick={() => setQuery({ id: null })} className="cursor-pointer self-start text-sm font-semibold text-accent lg:hidden">
        ← К списку
      </button>
      <div className="flex items-start justify-between gap-2.5">
        <span className="flex min-w-0 flex-col gap-1">
          <Label>Генерация #{shortId(g.id)}</Label>
          <span className="text-xl font-extrabold">{g.title}</span>
          <span className="text-sm text-soft">
            {userName(g.user)} · {g.kindLabel}
            {g.twin ? ' · с двойником' : ''}
          </span>
          {g.friend ? (
            <span className="text-sm text-soft">
              Дуо с{' '}
              <Link to={`/users/${g.friend.id}`} className="font-semibold">
                {userName(g.friend)}
              </Link>
            </span>
          ) : null}
          {g.kind === 'stickers' && g.status === 'succeeded' ? (
            g.stickerSet ? (
              <a href={`https://t.me/addstickers/${g.stickerSet}`} target="_blank" rel="noreferrer" className="text-sm font-semibold">
                Стикерпак t.me/addstickers/{g.stickerSet} →
              </a>
            ) : (
              <span className="text-sm text-bad">Пак не собрался: {g.stickerError ?? 'неизвестно'}</span>
            )
          ) : null}
          {g.fieldValues && Object.keys(g.fieldValues).length ? (
            <span className="text-xs text-muted">
              {Object.entries(g.fieldValues)
                .map(([k, v]) => `${k}: ${v}`)
                .join(' · ')}
            </span>
          ) : null}
        </span>
        <Status tone={st.tone}>{st.label}</Status>
      </div>

      <div className="flex flex-col">
        <TimelineItem tone="ok" label="Создана" note={`${dateTime(g.createdAt)} · ${genSum(g)}${g.hold ? ` · hold ${rub(g.hold)}` : ''}`} />
        {steps.map((s) => (
          <div key={s} className="flex flex-col">
            {g.totalSteps > 1 ? <span className="pb-1.5 pl-[26px] text-xs font-semibold tracking-[0.04em] text-muted">ШАГ {s + 1}</span> : null}
            {g.tasks
              .filter((t) => t.step === s)
              .map((t) => (
                <TimelineItem
                  key={t.id}
                  tone={taskTone(t)}
                  label={`${t.label || `Кадр ${t.index + 1}`} · ${t.status === 'succeeded' ? 'готов' : t.status === 'failed' ? 'не вышел' : t.status === 'submitted' ? 'у провайдера' : 'в очереди'}`}
                  note={[
                    t.previous?.provider ? `${PROVIDER[t.previous.provider] ?? t.previous.provider} упал (${t.previous.error ?? 'ошибка'}) → ` : '',
                    t.provider ? `${PROVIDER[t.provider] ?? t.provider}${t.model ? ` · ${t.model}` : ''}` : '',
                    t.submittedAt && t.finishedAt ? ` · ${seconds(Math.round((Date.parse(t.finishedAt) - Date.parse(t.submittedAt)) / 1000))}` : t.submittedAt ? ` · с ${time(t.submittedAt)}` : '',
                    t.error && t.status === 'failed' ? ` · ${ERR[t.error] ?? t.error}` : '',
                    t.regenerations ? ` · переделали ${t.regenerations}` : '',
                  ].join('')}
                  extra={t.qc.length ? <QcChecks checks={t.qc} /> : null}
                />
              ))}
          </div>
        ))}
        {!open ? (
          <TimelineItem
            tone={g.status === 'succeeded' ? 'ok' : 'bad'}
            label={g.status === 'succeeded' ? (g.refunded ? 'Готово, деньги вернули из админки' : 'Готово, юзеру отправлено') : g.refunded ? 'Возврат' : 'Не получилось'}
            note={g.finishedAt ? `${dateTime(g.finishedAt)}${g.released ? ` · вернули ${rub(g.released)}` : ''}` : ''}
            last
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-2 rounded-[14px] bg-field p-3.5 text-[13px]">
        <KV k="id" v={<span className="font-mono text-text2 select-all">{g.id}</span>} />
        {g.tasks
          .filter((t) => t.providerTaskId)
          .slice(0, 4)
          .map((t) => (
            <KV key={t.id} k={`task id${g.tasks.length > 1 ? ` · ${t.label || t.index + 1}` : ''}`} v={<span className="font-mono text-text2 select-all">{t.providerTaskId}</span>} />
          ))}
        <KV k="Списано (hold)" v={g.hold ? rub(g.hold) : g.free ? 'бесплатно' : g.test ? 'тест' : '—'} />
        {g.released ? <KV k="Возвращено" v={rub(g.released)} /> : null}
        <KV k="Себестоимость" v={`${g.costRub.toLocaleString('ru-RU')} ₽ · ${usd(g.costUsd)}`} />
        <KV k="Ошибка" v={<span className={firstErr ? 'text-bad' : 'text-muted'}>{firstErr ? (ERR[firstErr] ?? firstErr) : 'нет'}</span>} />
      </div>

      {g.results.length ? (
        <div className="grid grid-cols-3 gap-2">
          {g.results.map((r) => (
            <div key={r.url} className={cn('flex flex-col gap-1', r.type === 'audio' && 'col-span-3')}>
              <ResultMedia r={r} />
              {r.title || r.label ? <span className="truncate text-xs text-text2">{r.title || r.label}</span> : null}
            </div>
          ))}
        </div>
      ) : g.resultsHidden ? (
        <div className="flex flex-col items-start gap-2 rounded-[14px] border border-dashed border-line-strong p-3.5">
          <span className="text-sm text-soft">Результаты скрыты — на них лицо юзера</span>
          <Button size="sm" busy={busy === 'reveal'} onClick={() => void act('reveal', () => api.post(`/generations/${g.id}/results`), 'Просмотр записан в аудит')}>
            Показать (запишется в аудит)
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="accent"
          className="flex-[1_1_120px]"
          disabled={!open}
          title={open ? '' : 'Только для незаконченных'}
          busy={busy === 'restart'}
          onClick={() => void act('restart', () => api.post(`/generations/${g.id}/restart`), 'Кадры снова в очереди')}
        >
          Перезапустить
        </Button>
        <Button className="flex-[1_1_120px]" disabled={g.refunded || g.test} onClick={() => setConfirm('refund')}>
          {g.free ? 'Вернуть попытку' : 'Вернуть ₽'}
        </Button>
      </div>
      <Link to={`/users/${g.user.id}`} className="text-sm font-semibold">
        Открыть юзера →
      </Link>
      {g.trendId ? (
        <Link to={`/trends/${g.trendId}`} className="-mt-2 text-sm font-semibold">
          Открыть тренд →
        </Link>
      ) : null}

      <Modal open={confirm === 'refund'} onClose={() => setConfirm(null)} title={g.free ? 'Вернуть бесплатную попытку?' : `Вернуть ${rub(g.hold ?? g.price)}?`}>
        <span className="text-sm text-soft">
          {open ? 'Генерация остановится, поздние ответы провайдера не применятся. ' : ''}
          {g.free ? 'Юзер сможет сделать бесплатную ещё раз.' : 'Запишем release в журнал баланса, это видно в аудите.'} Юзеру придёт сообщение в бот.
        </span>
        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => setConfirm(null)}>
            Отмена
          </Button>
          <Button variant="accent" className="flex-1" busy={busy === 'refund'} onClick={() => void act('refund', () => api.post(`/generations/${g.id}/refund`), 'Вернули')}>
            Вернуть
          </Button>
        </div>
      </Modal>
    </Card>
  );
}

const KV = ({ k, v }: { k: string; v: React.ReactNode }) => (
  <span className="flex justify-between gap-2.5">
    <span className="shrink-0 text-muted">{k}</span>
    <span className="min-w-0 truncate text-right">{v}</span>
  </span>
);

function QcChecks({ checks }: { checks: AdminTask['qc'] }) {
  return (
    <span className="mt-1 flex flex-col gap-0.5">
      {checks.map((c, i) => (
        <span key={i} className={cn('text-xs', c.ok ? 'text-[#8FE8BF]' : 'text-warn')}>
          Проверка {i + 1}: {c.ok ? 'ок' : (c.reason ?? 'брак')}
          {c.face !== null ? ` · лицо ${c.face.toFixed(2)}` : ''}
          {c.vision ? ` · vision ${c.vision.ok ? 'ок' : (c.vision.reason ?? 'брак')}` : ''}
        </span>
      ))}
    </span>
  );
}

function TimelineItem({ tone, label, note, last, extra }: { tone: Tone; label: string; note: string; last?: boolean; extra?: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="flex w-3.5 flex-col items-center">
        <span className={cn('mt-1 size-3 shrink-0 rounded-full', TONE_BG[tone])} />
        {!last ? <span className="min-h-5 w-0.5 flex-1 bg-white/8" /> : null}
      </div>
      <div className="flex min-w-0 flex-col gap-0.5 pb-3.5">
        <span className="text-sm font-semibold">{label}</span>
        {note ? <span className="text-xs break-words text-muted">{note}</span> : null}
        {extra}
      </div>
    </div>
  );
}
